import React from "react";
import moment from "moment/min/moment-with-locales";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import "dayjs/locale/en";
import { colors } from "../components/Theme/WebTheme";
import {
  Grid,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  FormControl,
  Radio,
  RadioGroup,
  Modal,
  FormLabel,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import OtherPerson from "components/OtherPerson";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { getLangKey } from "common/src/other/getLangKey";
import { store } from "common";
import { normalizePhoneToE164 as commonNormalize, validateEmail as commonValidateEmail } from "common/src/other/authHelpers";
import { calculateEarnings as commonCalculateEarnings } from "common/src/other/EarningsCalculator";
import {
  WEB_MAIN_COLOR,
  WEB_SECONDORY_COLOR,
  MAIN_COLOR,
  SIDEBAR_COLOR,
  SECONDORY_COLOR,
  ADAPTIVE_COLOR,
  FONT_FAMILY,
} from "./constants";

export const calcEst = true;
export const showEst = true;
export const optionsRequired = false;

/**
 * [V2.0.0] Shared Formatting Helpers
 */

export const formatAmount = (value, decimal, country, locale) => {
  const number = parseFloat(value || 0);
  let locales = "en-US";
  if (locale && locale.startsWith("fr")) locales = "fr-FR";
  else if (country === "Vietnam") locales = "vi-VN";
  else if (country === "France") locales = "fr-FR";
  
  return number.toLocaleString(locales, {
    minimumFractionDigits: decimal ?? 2,
    maximumFractionDigits: decimal ?? 2,
  });
};

export const getVehicleName = (name, t) => {
  if (!name) return "";
  return t(getLangKey(name))
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

export const getSurchargeName = (name, t, currentLang) => {
  if (!name) return t ? t("surcharge_pct_label", "Majoration") : "Majoration";

  const lang = (
    currentLang ||
    (typeof window !== "undefined" && window.localStorage?.getItem("i18nextLng")) ||
    "fr"
  )
    .split("-")[0]
    .toLowerCase();

  const isEn = lang.startsWith("en");

  // Support for legacy "FR / EN" or "FR/EN" in database
  if (typeof name === "string" && name.includes("/")) {
    const parts = name.split("/").map((p) => p.trim());
    if (isEn && parts.length > 1 && parts[1]) {
      return parts[1];
    }
    return parts[0];
  }

  const key = getLangKey(name);

  // 1) Try i18n
  if (t) {
    const translated = t(key);
    if (translated && translated !== key) {
      return translated;
    }
  }

  // 2) Direct fallback to Redux store languagedata for instant reactivity without reload
  try {
    const state = store?.getState?.();
    const languagedata = state?.languagedata;
    if (languagedata) {
      const match =
        languagedata.langMap?.[lang] ||
        languagedata.langMap?.[lang === "en" ? "en-US" : "fr-FR"] ||
        languagedata.langlist?.find((l) =>
          String(l?.langLocale || "").toLowerCase().startsWith(lang)
        )?.keyValuePairs;

      if (match && match[key]) {
        return match[key];
      }
    }
  } catch (e) {
    // Ignore store read issues
  }

  // 3) Universal keyword dictionary fallback
  const EN_DICT = [
    { regex: /dimanche|sunday/i, label: "Sunday Surcharge" },
    { regex: /samedi|saturday/i, label: "Saturday Surcharge" },
    { regex: /week-?end/i, label: "Weekend Surcharge" },
    { regex: /nuit|night/i, label: "Night Surcharge" },
    { regex: /f[eé]ri[eé]|holiday/i, label: "Public Holiday Surcharge" },
    { regex: /pointe|peak/i, label: "Peak Hours Surcharge" },
    { regex: /nouvel\s*an|saint-?sylvestre|new\s*year/i, label: "New Year's Eve Surcharge" },
    { regex: /no[eë]l|christmas/i, label: "Christmas Surcharge" },
    { regex: /neige|snow|verglas|ice/i, label: "Snow & Ice Surcharge" },
    { regex: /haute\s*saison|high\s*season/i, label: "High Season Surcharge" },
  ];
  const FR_DICT = [
    { regex: /sunday|dimanche/i, label: "Majoration Dimanche" },
    { regex: /saturday|samedi/i, label: "Majoration Samedi" },
    { regex: /weekend|week-?end/i, label: "Majoration Week-end" },
    { regex: /night|nuit/i, label: "Majoration Nuit" },
    { regex: /public\s*holiday|bank\s*holiday|holiday|f[eé]ri[eé]/i, label: "Majoration Jour Férié" },
    { regex: /peak|pointe/i, label: "Majoration Heures de Pointe" },
    { regex: /new\s*year|nouvel\s*an|saint-?sylvestre/i, label: "Majoration Nouvel An" },
    { regex: /christmas|no[eë]l/i, label: "Majoration Noël" },
    { regex: /snow|ice|neige|verglas/i, label: "Majoration Neige & Verglas" },
    { regex: /high\s*season|haute\s*saison/i, label: "Majoration Haute Saison" },
  ];

  const dict = isEn ? EN_DICT : FR_DICT;
  for (const entry of dict) {
    if (entry.regex.test(name)) {
      return entry.label;
    }
  }

  if (isEn && /^majoration\s+/i.test(name)) {
    return `${name.replace(/^majoration\s+/i, "").trim()} Surcharge`;
  }

  return name;
};

export const getVacationMessage = (vacationMode, t, currentLang) => {
  if (!vacationMode) return "";
  const lang = (
    currentLang ||
    (typeof window !== "undefined" && window.localStorage?.getItem("i18nextLng")) ||
    "fr"
  )
    .split("-")[0]
    .toLowerCase();

  const isEn = lang.startsWith("en");

  if (vacationMode.message) {
    const rawMsg = vacationMode.message;
    // Check if legacy message with slash "FR / EN"
    if (typeof rawMsg === "string" && rawMsg.includes("/")) {
      const parts = rawMsg.split("/").map((p) => p.trim());
      if (isEn && parts.length > 1 && parts[1]) {
        return parts[1];
      }
      return parts[0];
    }

    if (t) {
      const key = getLangKey(rawMsg);
      const translated = t(key);
      if (translated && translated !== key) {
        return translated;
      }
    }

    // Direct Redux fallback
    try {
      const state = store?.getState?.();
      const languagedata = state?.languagedata;
      if (languagedata) {
        const key = getLangKey(rawMsg);
        const match =
          languagedata.langMap?.[lang] ||
          languagedata.langMap?.[isEn ? "en-US" : "fr-FR"] ||
          languagedata.langlist?.find((l) =>
            String(l?.langLocale || "").toLowerCase().startsWith(lang)
          )?.keyValuePairs;
        if (match && match[key]) {
          return match[key];
        }
      }
    } catch (e) {
      // Ignore store errors
    }

    return rawMsg;
  }

  // Default auto-generated message
  if (vacationMode.startDate && vacationMode.endDate) {
    const dateFormat = isEn ? "MMMM D, YYYY" : "D MMMM YYYY";
    const startFormatted = dayjs(vacationMode.startDate)
      .locale(isEn ? "en" : "fr")
      .format(dateFormat);
    const endFormatted = dayjs(vacationMode.endDate)
      .locale(isEn ? "en" : "fr")
      .format(dateFormat);
    return t
      ? t(
          "vacation_banner_default_dates",
          "Notre service est temporairement indisponible du {{start}} au {{end}}. Vous pouvez dès maintenant réserver vos transferts pour les dates suivantes.",
          {
            start: startFormatted,
            end: endFormatted,
          }
        )
      : `Notre service est temporairement indisponible du ${startFormatted} au ${endFormatted}.`;
  }

  return t
    ? t(
        "vacation_banner_default",
        "Notre service est temporairement suspendu pour congés. Vous pouvez dès maintenant réserver vos trajets pour des dates ultérieures."
      )
    : "Notre service est temporairement suspendu pour congés.";
};

export const normalizePhoneToE164 = commonNormalize;
export const validateEmail = commonValidateEmail;
export const calculateEarnings = commonCalculateEarnings;

export {
  WEB_MAIN_COLOR,
  WEB_SECONDORY_COLOR,
  MAIN_COLOR,
  SIDEBAR_COLOR,
  SECONDORY_COLOR,
  ADAPTIVE_COLOR,
  FONT_FAMILY,
};

// [V2.0.0] The logic below now uses exported common helpers for phone/email validation.

// NEW: PWA Detection
export const isPWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone || document.referrer.includes('android-app://');
};

// NEW: iOS Detection
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// NEW: localStorage TTL Helpers
export const setLocalWithTTL = (key, value, ttlInMs) => {
  const item = {
    value: value,
    expiry: Date.now() + ttlInMs,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

export const getLocalWithTTL = (key) => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;
  try {
    const item = JSON.parse(itemStr);
    if (Date.now() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  } catch (e) {
    localStorage.removeItem(key);
    return null;
  }
};

export const bookingHistoryColumns = (
  role,
  settings,
  t,
  isRTL,
  formatAmount,
) => [
    { title: t("booking_ref"), field: "reference" },
    {
      title: t("booking_date"),
      field: "bookingDate",
      render: (rowData) =>
        rowData.bookingDate ? moment(rowData.bookingDate).format("lll") : null,
    },
    {
      title: t("car_type"),
      field: "carType",
      render: (rowData) =>
        rowData.carType
          ? t(getLangKey(rowData.carType))
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())
          : null,
    },
    { title: t("assign_driver"), field: "driver_name" },
    {
      title: t("booking_status_web"),
      field: "status",
      render: (rowData) => (
        <div
          style={{
            backgroundColor:
              rowData.status === "CANCELLED"
                ? colors.RED
                : rowData.status === "COMPLETE"
                  ? colors.GREEN
                  : colors.YELLOW,
            color: colors.ST_WHITE,
            padding: 7,
            borderRadius: "15px",
            fontWeight: "bold",
            width: "150px",
            margin: "auto",
          }}
        >
          {t(rowData.status)}
        </div>
      ),
    },
    {
      title: t("booking_type"),
      field: "booking_type",
      hidden: settings.appCat === "delivery" ? false : true,
      render: (rowData) => (
        <div>{rowData?.deliveryWithBid ? t("bid") : t("system_price")}</div>
      ),
    },
    {
      title: t("otp"),
      field: "otp",
      render: (rowData) => <div>{rowData.otp ? rowData.otp : t("false")}</div>,
    },
    {
      title: t("roundTrip"),
      field: "roundTrip",
      hidden: settings.appCat === "bidcab" ? false : true,
      render: (rowData) => <div>{rowData?.roundTrip ? t("yes") : t("no")}</div>,
    },
    {
      title: t("trip_cost"),
      field: "trip_cost",
      render: (rowData) =>
        settings.appCat === "delivery"
          ? role === "customer" &&
            ["NEW", "PAYMENT_PENDING"].indexOf(rowData.status) !== -1
            ? t("bid").toUpperCase()
            : rowData.trip_cost
              ? settings.swipe_symbol
                ? formatAmount(
                  rowData.trip_cost,
                  settings.decimal,
                  settings.country,
                ) +
                " " +
                settings.symbol
                : settings.symbol +
                " " +
                formatAmount(
                  rowData.trip_cost,
                  settings.decimal,
                  settings.country,
                )
              : settings.swipe_symbol
                ? "0 " + settings.symbol
                : settings.symbol + " 0"
          : rowData.trip_cost
            ? settings.swipe_symbol
              ? formatAmount(
                rowData.trip_cost,
                settings.decimal,
                settings.country,
              ) +
              " " +
              settings.symbol
              : settings.symbol +
              " " +
              formatAmount(
                rowData.trip_cost,
                settings.decimal,
                settings.country,
              )
            : settings.swipe_symbol
              ? "0 " + settings.symbol
              : settings.symbol + " 0",
    },
  ];

export const BookingModalBody = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    classes,
    tripInstructions,
    handleChange,
    roundTrip,
    deliveryWithBid,
    auth,
    profileData,
    instructionData,
    otherPerson,
    setOtherPerson,
    offerFare,
    estimate,
    minimumPrice,
    settings,
    displayOfferFare,
    handleOfferFareChange,
  } = props;

  // Vietnamese number input support
  const isVietnamese = settings?.country === "Vietnam";
  return (
    <Grid item xs={12}>
      <span>
        {settings.appCat === "delivery" ? (
          <Typography
            component="h2"
            variant="h5"
            style={{
              marginTop: 15,
              color: colors.BLACK,
              fontFamily: FONT_FAMILY,
            }}
          >
            {t("delivery_information")}
          </Typography>
        ) : settings.appCat === "bidcab" ? (
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}
          >
            <FormControl component="fieldset">
              <FormLabel component="legend">
                {
                  <Typography style={{ fontFamily: FONT_FAMILY }}>
                    {t("roundTrip")}
                  </Typography>
                }
              </FormLabel>
              <RadioGroup
                name="roundTrip"
                value={roundTrip}
                onChange={handleChange}
              >
                <FormControlLabel
                  key={"key0"}
                  value={0}
                  control={<Radio />}
                  label={
                    <Typography style={{ fontFamily: FONT_FAMILY }}>
                      {t("no")}
                    </Typography>
                  }
                />
                <FormControlLabel
                  key={"key1"}
                  value={1}
                  control={<Radio />}
                  label={
                    <Typography style={{ fontFamily: FONT_FAMILY }}>
                      {t("yes")}
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        ) : null}

        {auth.profile.usertype === "customer" && !auth.profile.firstName ? (
          <Grid item xs={12}>
            <TextField
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              variant="outlined"
              margin="normal"
              required={auth.profile.firstName ? false : true}
              fullWidth
              id="firstName"
              label={t("firstname")}
              name="firstName"
              autoComplete="firstName"
              onChange={handleChange}
              value={profileData.firstName}
              autoFocus
              className={isRTL === "rtl" ? classes.inputRtl : classes.textField}
              style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
            />
          </Grid>
        ) : null}
        {auth.profile.usertype === "customer" && !auth.profile.lastName ? (
          <Grid item xs={12}>
            <TextField
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              variant="outlined"
              margin="normal"
              required={auth.profile.lastName ? false : true}
              fullWidth
              id="lastName"
              label={t("lastname")}
              name="lastName"
              autoComplete="lastName"
              onChange={handleChange}
              value={profileData.lastName}
              className={isRTL === "rtl" ? classes.inputRtl : classes.textField}
              style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
            />
          </Grid>
        ) : null}
        {auth.profile.usertype === "customer" && !auth.profile.email ? (
          <Grid item xs={12}>
            <TextField
              InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
              variant="outlined"
              margin="normal"
              required={auth.profile.email ? false : true}
              fullWidth
              id="email"
              label={t("email")}
              name="email"
              autoComplete="email"
              onChange={handleChange}
              value={profileData.email}
              className={isRTL === "rtl" ? classes.inputRtl : classes.textField}
              style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
            />
          </Grid>
        ) : null}

        <OtherPerson
          classes={classes}
          otherPerson={otherPerson}
          handleChange={handleChange}
          setOtherPerson={setOtherPerson}
          instructionData={instructionData}
        />
        {settings.appCat === "delivery" ? (
          <>
            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                variant="outlined"
                margin="normal"
                fullWidth
                id="pickUpInstructions"
                label={t("pickUpInstructions")}
                name="pickUpInstructions"
                autoComplete="pickUpInstructions"
                onChange={handleChange}
                value={instructionData.pickUpInstructions}
                className={
                  isRTL === "rtl" ? classes.inputRtl : classes.textField
                }
                style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                variant="outlined"
                margin="normal"
                fullWidth
                id="deliveryInstructions"
                label={t("deliveryInstructions")}
                name="deliveryInstructions"
                autoComplete="deliveryInstructions"
                onChange={handleChange}
                value={instructionData.deliveryInstructions}
                className={
                  isRTL === "rtl" ? classes.inputRtl : classes.textField
                }
                style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
              />
            </Grid>
            {settings.bookingFlow === "0" &&
              auth.profile.usertype === "customer" ? (
              <Grid item xs={12}>
                <RadioGroup
                  row
                  name="bookingFlow"
                  value={deliveryWithBid}
                  onChange={handleChange}
                >
                  <FormControlLabel
                    key={"key0"}
                    value={0}
                    control={<Radio />}
                    label={
                      <Typography className={classes.typography}>
                        {t("system_price")}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    key={"key1"}
                    value={1}
                    control={<Radio />}
                    label={
                      <Typography className={classes.typography}>
                        {t("bid")}
                      </Typography>
                    }
                  />
                </RadioGroup>
              </Grid>
            ) : null}
          </>
        ) : settings.appCat === "bidcab" ? (
          <>
            <Grid item xs={12}>
              <TextField
                InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                variant="outlined"
                margin="normal"
                fullWidth
                id="tripInstructions"
                label={t("tripInstructions")}
                name="tripInstructions"
                autoComplete="tripInstructions"
                type="numeric"
                onChange={handleChange}
                value={tripInstructions}
                className={
                  isRTL === "rtl"
                    ? [classes.inputRtl, classes.rightRty]
                    : classes.textField
                }
                style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
              />
            </Grid>
            {settings && !settings.coustomerBidPrice ? (
              <>
                <Grid item xs={12}>
                  <TextField
                    InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    id="offerFare"
                    label={t("offer_your_fare")}
                    name="offerFare"
                    autoComplete="offerFare"
                    type={isVietnamese ? "text" : "numeric"}
                    onChange={
                      isVietnamese ? handleOfferFareChange : handleChange
                    }
                    value={isVietnamese ? displayOfferFare : offerFare}
                    className={
                      isRTL === "rtl"
                        ? [classes.inputRtl, classes.rightRty]
                        : classes.textField
                    }
                    style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
                  />
                </Grid>
                {parseFloat(minimumPrice) >= offerFare && offerFare > 0 ? (
                  <Typography
                    style={{ fontFamily: FONT_FAMILY, color: colors.RED }}
                  >
                    {t("minimum_price")}: {minimumPrice}
                  </Typography>
                ) : (
                  <Typography style={{ fontFamily: FONT_FAMILY }}>
                    {t("recomendet_price")}:{" "}
                    {estimate ? estimate.estimateFare : null}
                  </Typography>
                )}
              </>
            ) : null}
          </>
        ) : null}
      </span>
    </Grid>
  );
};

export const validateBookingObj = (
  t,
  bookingObject,
  instructionData,
  settings,
) => {
  if (settings.appCat === "bidcab") {
    delete bookingObject.driverEstimates;
  }
  return { bookingObject };
};

export const PanicSettings = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { classes, data, handleTextChange } = props;
  return (
    <span>
      <Typography
        component="h1"
        variant="h5"
        style={{
          marginTop: "15px",
          textAlign: isRTL === "rtl" ? "right" : "left",
          fontFamily: FONT_FAMILY,
        }}
      >
        {t("panic_num")}
      </Typography>
      <TextField
        InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
        variant="outlined"
        margin="normal"
        fullWidth
        id="panic"
        label={t("panic_num")}
        className={
          isRTL === "rtl"
            ? [classes.rootRtl_1, classes.right]
            : classes.textField
        }
        name="panic"
        autoComplete="panic"
        onChange={handleTextChange}
        value={data.panic}
      />
    </span>
  );
};

export const DispatchSettings = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { autoDispatch, onChange, data } = props;
  return data.appCat === "bidcab" ? null : (
    <FormControlLabel
      style={{ flexDirection: isRTL === "rtl" ? "row-reverse" : "row" }}
      control={
        <Switch
          checked={autoDispatch}
          onChange={onChange}
          name="autoDispatch"
          color="primary"
        />
      }
      label={
        <Typography style={{ fontFamily: FONT_FAMILY }}>
          {t("auto_dispatch")}
        </Typography>
      }
    />
  );
};

export const BookingImageSettings = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { data, handleSwitchChange } = props;
  return data.appCat === "delivery" ? (
    <span>
      <FormControlLabel
        style={{
          marginTop: "10px",
          flexDirection: isRTL === "rtl" ? "row-reverse" : "row",
        }}
        control={
          <Switch
            checked={data.AllowDeliveryPickupImageCapture}
            onChange={handleSwitchChange}
            name="AllowDeliveryPickupImageCapture"
            color="primary"
          />
        }
        label={
          <Typography style={{ fontFamily: FONT_FAMILY }}>
            {t("allow_del_pkp_img")}
          </Typography>
        }
      />
      <FormControlLabel
        style={{
          marginTop: "10px",
          flexDirection: isRTL === "rtl" ? "row-reverse" : "row",
        }}
        control={
          <Switch
            checked={data.AllowFinalDeliveryImageCapture}
            onChange={handleSwitchChange}
            name="AllowFinalDeliveryImageCapture"
            color="primary"
          />
        }
        label={
          <Typography style={{ fontFamily: FONT_FAMILY }}>
            {t("allow_del_final_img")}
          </Typography>
        }
      />
    </span>
  ) : null;
};

export const carTypeColumns = (t, isRTL, onClick, formatAmount, settings) => [
  {
    title: t("name"),
    field: "name",
    render: (rowData) =>
      rowData.name ? (
        <Typography style={{ fontFamily: FONT_FAMILY }}>
          {t(getLangKey(rowData.name))
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase())}
        </Typography>
      ) : null,
  },
  {
    title: t("image"),
    field: "image",
    initialEditValue:
      "https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png",
    render: (rowData) =>
      rowData.image ? (
        <button
          onClick={() => {
            onClick(rowData);
          }}
        >
          <img alt="CarImage" src={rowData.image} style={{ width: 50 }} />
        </button>
      ) : null,
  },
  {
    title: t("base_fare"),
    field: "base_fare",
    type: "numeric",
    initialEditValue: 0,
    render: (rowData) =>
      rowData.base_fare
        ? formatAmount(rowData.base_fare, settings.decimal, settings.country)
        : 0,
  },
  {
    title: t("rate_per_unit_distance"),
    field: "rate_per_unit_distance",
    type: "numeric",
    initialEditValue: 0,
    render: (rowData) =>
      rowData.rate_per_unit_distance
        ? formatAmount(
          rowData.rate_per_unit_distance,
          settings.decimal,
          settings.country,
        )
        : 0,
  },
  {
    title: t("rate_per_hour"),
    field: "rate_per_hour",
    type: "numeric",
    initialEditValue: 0,
    render: (rowData) =>
      rowData.rate_per_hour
        ? formatAmount(
          rowData.rate_per_hour,
          settings.decimal,
          settings.country,
        )
        : 0,
  },
  {
    title: t("min_fare"),
    field: "min_fare",
    type: "numeric",
    initialEditValue: 0,
    render: (rowData) =>
      rowData.min_fare
        ? formatAmount(rowData.min_fare, settings.decimal, settings.country)
        : 0,
  },
  {
    title: t("convenience_fee"),
    field: "convenience_fees",
    type: "numeric",
    initialEditValue: 0,
    render: (rowData) =>
      rowData.convenience_fees
        ? formatAmount(
          rowData.convenience_fees,
          settings.decimal,
          settings.country,
        )
        : 0,
  },
  {
    title: t("convenience_fee_type"),
    field: "convenience_fee_type",
    lookup: { flat: t("flat"), percentage: t("percentage") },
  },
  {
    title: t("fleet_admin_comission"),
    field: "fleet_admin_fee",
    type: "numeric",
    initialEditValue: 0,
    render: (rowData) =>
      rowData.fleet_admin_fee
        ? formatAmount(
          rowData.fleet_admin_fee,
          settings.decimal,
          settings.country,
        )
        : 0,
  },
  {
    title: t("extra_info"),
    field: "extra_info",
  },
  { title: t("position"), field: "pos", type: "numeric", defaultSort: "asc" },
];

export const acceptBid = (selectedBooking, selectedBidder, settings) => {
  if (settings.appCat === "delivery") {
    let bookingObj = { ...selectedBooking };
    bookingObj.selectedBid = bookingObj.driverOffers[selectedBidder];
    const uid = bookingObj.selectedBid.driver;
    for (let key in bookingObj.driverOffers) {
      if (key !== uid) {
        delete bookingObj.driverOffers[key];
      }
    }
    for (let key in bookingObj.requestedDrivers) {
      if (key !== uid) {
        delete bookingObj.requestedDrivers[key];
      }
    }
    return bookingObj;
  } else if (settings.appCat === "bidcab") {
    let bookingObj = { ...selectedBooking };
    bookingObj.selectedBid = bookingObj.driverOffers[selectedBidder];
    const uid = bookingObj.selectedBid.driver;
    for (let key in bookingObj.driverOffers) {
      if (key !== uid) {
        delete bookingObj.driverOffers[key];
      }
    }
    for (let key in bookingObj.requestedDrivers) {
      if (key !== uid) {
        delete bookingObj.requestedDrivers[key];
      }
    }
    return bookingObj;
  } else {
    return null;
  }
};

export const BidModal = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    ref,
    role,
    selectedBooking,
    bidModalStatus,
    handleBidModalClose,
    classes,
    settings,
    acceptBid,
    selectedBidder,
    handleChange,
    formatAmount,
  } = props;
  if (settings.appCat === "delivery" || settings.appCat === "bidcab") {
    return selectedBooking &&
      selectedBooking.driverOffers &&
      role === "customer" ? (
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={bidModalStatus}
        onClose={handleBidModalClose}
        className={classes.modal}
        container={() => ref}
      >
        <Grid container spacing={2} className={classes.paper}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <FormControl component="fieldset">
              <FormLabel
                component="legend"
                style={{
                  textAlign: isRTL === "rtl" ? "right" : "left",
                  fontFamily: FONT_FAMILY,
                }}
              >
                {t("payment")}
              </FormLabel>
              <RadioGroup
                name="selectedBidder"
                value={selectedBidder}
                onChange={handleChange}
              >
                {Object.keys(selectedBooking.driverOffers).map((key) => (
                  <FormControlLabel
                    key={key}
                    value={key}
                    control={<Radio />}
                    label={
                      <Typography className={classes.typography}>
                        {selectedBooking.driverOffers[key].driver_name +
                          " - " +
                          (settings.swipe_symbol === false
                            ? settings.symbol
                            : "") +
                          formatAmount(
                            selectedBooking.driverOffers[key].trip_cost,
                            settings.decimal,
                            settings.country,
                          ) +
                          (settings.swipe_symbol ? settings.symbol : "")}{" "}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
          >
            <Button
              onClick={handleBidModalClose}
              variant="contained"
              color="primary"
              style={{ fontFamily: FONT_FAMILY }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{
                ...(isRTL === "rtl" ? { marginRight: 10 } : { marginLeft: 10 }),
                fontFamily: FONT_FAMILY,
              }}
              onClick={acceptBid}
            >
              {t("selectBid")}
            </Button>
          </Grid>
        </Grid>
      </Modal>
    ) : null;
  } else {
    return null;
  }
};

export const downloadCsv = (data, fileName) => {
  const finalFileName = fileName.endsWith(".csv")
    ? fileName
    : `${fileName}.csv`;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
  a.setAttribute("download", finalFileName);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const DeliveryFlow = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { data, handleChange } = props;
  return data.appCat === "delivery" ? (
    <span>
      <Typography
        component="h1"
        variant="h5"
        style={{
          marginTop: "15px",
          textAlign: isRTL === "rtl" ? "right" : "left",
          fontFamily: FONT_FAMILY,
        }}
      >
        {t("booking_type")}
      </Typography>

      <Grid
        item
        xs={12}
        sm={12}
        md={12}
        lg={12}
        style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}
      >
        <FormControl component="fieldset">
          <RadioGroup
            row
            name="booking_type"
            value={data.bookingFlow}
            onChange={handleChange}
          >
            <FormControlLabel
              key={"key0"}
              value={0}
              control={<Radio />}
              label={
                <Typography fontFamily={FONT_FAMILY}>
                  {t("system_price_with_bid")}
                </Typography>
              }
            />
            <FormControlLabel
              key={"key1"}
              value={1}
              control={<Radio />}
              label={
                <Typography fontFamily={FONT_FAMILY}>
                  {t("system_price")}
                </Typography>
              }
            />
            <FormControlLabel
              key={"key2"}
              value={2}
              control={<Radio />}
              label={
                <Typography fontFamily={FONT_FAMILY}>{t("bid")}</Typography>
              }
            />
          </RadioGroup>
        </FormControl>
      </Grid>
    </span>
  ) : null;
};

export const CustomerBidSettings = (props) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    data,
    classes,
    handleSwitchChange,
    handleChangeBidPriceType,
    handleCustomerPrice,
    settings,
    displayBidPrice,
    handleBidPriceChange,
  } = props;

  // Vietnamese number input support
  const isVietnamese = settings?.country === "Vietnam";
  return data.appCat === "bidcab" ? (
    <span>
      <Typography
        component="h1"
        variant="h5"
        style={{
          marginTop: "15px",
          textAlign: isRTL === "rtl" ? "right" : "left",
          fontFamily: FONT_FAMILY,
          marginBottom: 10,
        }}
      >
        {t("customer_initiate_bid_price_setting")}
      </Typography>

      <FormControlLabel
        style={{
          marginLeft: "6px",
          marginBottom: "10px",
          flexDirection: isRTL === "rtl" ? "row-reverse" : "row",
        }}
        control={
          <Switch
            checked={data.coustomerBidPrice}
            onChange={handleSwitchChange}
            name="coustomerBidPrice"
            color="primary"
          />
        }
        label={
          <Typography className={classes.typography}>
            {t("disable_coustomerBidPrice")}
          </Typography>
        }
      />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={6} xl={6}>
          <FormControl
            fullWidth
            style={{ direction: isRTL === "rtl" ? "rtl" : "ltr", marginTop: 1 }}
          >
            <InputLabel
              id="coustomerBidPrice_type"
              className={isRTL === "rtl" ? classes.right : ""}
              sx={{
                "&.Mui-focused": {
                  color: WEB_MAIN_COLOR(props?.settings?.appCat),
                },
              }}
            >
              {
                <Typography className={classes.typography}>
                  {t("coustomerBidPrice_type")}
                </Typography>
              }
            </InputLabel>
            <Select
              labelId="coustomerBidPrice_type"
              id="coustomerBidPrice_type"
              value={data?.coustomerBidPriceType || ""}
              label={t("coustomerBidPrice_type")}
              onChange={handleChangeBidPriceType}
              className={
                isRTL === "rtl" ? classes.selectField_rtl : classes.selectField
              }
            >
              <MenuItem
                style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
                value={"percentage"}
              >
                {
                  <Typography className={classes.typography}>
                    {t("percentage")}
                  </Typography>
                }
              </MenuItem>
              <MenuItem
                style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
                value={"flat"}
              >
                {
                  <Typography className={classes.typography}>
                    {t("flat")}
                  </Typography>
                }
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <TextField
            InputLabelProps={{ style: { fontFamily: FONT_FAMILY } }}
            className={isRTL === "rtl" ? classes.rootRtl : classes.textField}
            variant="outlined"
            // /margin="normal"
            fullWidth
            id="bidprice"
            label={t("bid_lower_price")}
            name="bidprice"
            autoComplete="bidprice"
            type={isVietnamese ? "text" : "number"}
            onChange={isVietnamese ? handleBidPriceChange : handleCustomerPrice}
            value={isVietnamese ? displayBidPrice : data.bidprice}
          />
        </Grid>
      </Grid>
    </span>
  ) : null;
};

export const formatCurrency = (amount, settings, locale, specificSymbol) => {
  if (amount === null || amount === undefined) return "";
  
  // Use centralized formatAmount for consistent locale-aware decimal separators
  const formatted = formatAmount(amount, settings.decimal, settings.country, locale);
  let symbol = specificSymbol || settings.symbol || "€";
  if (symbol === "EUR" || symbol === "eur") symbol = "€";
  if (symbol === "USD" || symbol === "usd") symbol = "$";
  if (symbol === "GBP" || symbol === "gbp") symbol = "£";

  // Force position based on language
  if (locale && (locale.startsWith("fr") || locale.startsWith("FR"))) {
    return `${formatted} ${symbol}`;
  }
  if (locale && (locale.startsWith("en") || locale.startsWith("EN"))) {
    return `${symbol}${formatted}`;
  }

  // Fallback to settings
  return settings.swipe_symbol
    ? `${formatted} ${symbol}`
    : `${symbol}${formatted}`;
};

/**
 * Formats duration in minutes to a human-readable string (e.g., 2 h 18 min).
 * @param {number} totalMinutes 
 * @returns {string}
 */
export const formatDurationReadable = (totalMinutes) => {
  const mins = typeof totalMinutes === "number" ? totalMinutes : parseFloat(totalMinutes);
  if (!Number.isFinite(mins) || mins <= 0) return "";

  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);

  if (h > 0) {
    return m > 0 ? `${h} h ${m} min` : `${h} h`;
  }
  return `${m} min`;
};
